import * as THREE from "three/webgpu"
import { FolderApi } from "tweakpane"

interface LightSettings {
  alColor: string
  alIntensity: number
  dlColor: string
  dlIntensity: number
  dlPosition: { x: number; y: number; z: number }
  plColor: string
  plIntensity: number
  plPosition: { x: number; y: number; z: number }
}

export default class Light {
  scene: THREE.Scene
  settings: LightSettings
  debugFolder?: FolderApi

  ambientLight!: THREE.AmbientLight
  directionalLight!: THREE.DirectionalLight
  pointLight!: THREE.PointLight

  static defaultSettings: LightSettings = {
    alColor: "#f0f2fb",
    alIntensity: 0.59,
    dlColor: "#6f86a4",
    dlIntensity: 1,
    dlPosition: { x: -0.2, y: 1.3, z: -1.9 },
    plColor: "#9cd0ff",
    plIntensity: 0.6,
    plPosition: { x: 0.8, y: 1.8, z: -10 },
  }

  constructor({
    scene,
    settings,
    debugFolder,
  }: {
    scene: THREE.Scene
    settings?: Partial<LightSettings>
    debugFolder?: FolderApi
  }) {
    this.scene = scene
    this.settings = { ...Light.defaultSettings, ...settings }
    this.debugFolder = debugFolder

    this.setupLights()
    this.setDebug()
  }

  setupLights() {
    // Ambient light
    this.ambientLight = new THREE.AmbientLight(
      this.settings.alColor,
      this.settings.alIntensity,
    )
    this.scene.add(this.ambientLight)

    // Directional light
    this.directionalLight = new THREE.DirectionalLight(
      this.settings.dlColor,
      this.settings.dlIntensity,
    )
    this.directionalLight.position.set(
      this.settings.dlPosition.x,
      this.settings.dlPosition.y,
      this.settings.dlPosition.z,
    )
    this.scene.add(this.directionalLight)

    // Point light
    this.pointLight = new THREE.PointLight(
      this.settings.plColor,
      this.settings.plIntensity,
    )
    this.pointLight.position.set(
      this.settings.plPosition.x,
      this.settings.plPosition.y,
      this.settings.plPosition.z,
    )
    this.scene.add(this.pointLight)
  }

  setDebug() {
    if (!this.debugFolder) {
      return
    }

    const lightFolder = this.debugFolder.addFolder({
      title: "💡 Lights",
      expanded: false,
    })

    lightFolder
      .addBinding(this.settings, "alColor", {
        label: "Ambient Light Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.ambientLight.color.set(ev.value)
      })

    lightFolder
      .addBinding(this.settings, "alIntensity", {
        label: "Ambient Light Intensity",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.ambientLight.intensity = ev.value
      })

    lightFolder
      .addBinding(this.settings, "dlColor", {
        label: "Dir Light Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.directionalLight.color.set(ev.value)
      })

    lightFolder
      .addBinding(this.settings, "dlPosition", {
        label: "Dir Light Position",
        x: { min: -10, max: 10, step: 0.1 },
        y: { min: -10, max: 10, step: 0.1 },
        z: { min: -10, max: 10, step: 0.1 },
      })
      .on("change", (ev) => {
        this.directionalLight.position.set(ev.value.x, ev.value.y, ev.value.z)
      })

    lightFolder
      .addBinding(this.settings, "dlIntensity", {
        label: "Dir Light Intensity",
        min: 0,
        max: 1,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.directionalLight.intensity = ev.value
      })

    lightFolder
      .addBinding(this.settings, "plColor", {
        label: "Point Light Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.pointLight.color.set(ev.value)
      })

    lightFolder
      .addBinding(this.settings, "plPosition", {
        label: "Point Light Position",
        x: { min: -10, max: 10, step: 0.1 },
        y: { min: -10, max: 10, step: 0.1 },
        z: { min: -10, max: 10, step: 0.1 },
      })
      .on("change", (ev) => {
        this.pointLight.position.set(ev.value.x, ev.value.y, ev.value.z)
      })

    lightFolder
      .addBinding(this.settings, "plIntensity", {
        label: "Point Light Intensity",
        min: 0,
        max: 20,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.pointLight.intensity = ev.value
      })
  }
}
